import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Backdrop from '../modal-sections/Backdrop';
import {ModalHeader} from '../modal-sections/ModalHeader';
import {ModalTitle} from '../modal-sections/ModalTitle';
import {ModalContainer} from '../modal-sections/ModalContainer';
import {useHotkeys} from '../../../hotkeys.js';
import {Icon} from '../../elements/Icon/Icon';
import {Btn} from '../../elements/button/btn-type/Btn';
import {modalSize} from '../modal-condition/modalSize';
import {ModalFooter} from '../modal-sections/ModalFooter';
import {ModalBody} from '../modal-sections/ModalBody';
import {ScrollBar} from '../../../shared-components/mixin/scroll/scrollBar';
import {GradientGimmickBar} from '../../../shared-components/mixin/gradientGimmick';

export const Dialog = ({show, dialogRef, ...props}) => {
    if (!show) return null;
    return <Modal {...props} dialogRef={dialogRef} />;
};

function Modal({size, position, children, onHide, title, overflowUnset, overflowNone, backdrop, dialogRef}) {
    useHotkeys(
        () => ({
            Escape: onHide,
        }),
        [onHide],
    );

    return ReactDOM.createPortal(
        <ModalContainer popup={true} show={true}>
            <ModalPopupDialog popup={true} position={position} ref={dialogRef}>
                <ModalContent overflowNone={overflowNone} overflowUnset={overflowUnset} size={size} popup={true}>
                    {title && (
                        <ModalHeader>
                            <ModalTitle>{title}</ModalTitle>
                            <Btn link color="blank" onClick={onHide} type="button">
                                <Icon type="lg" name="Close" />
                            </Btn>
                        </ModalHeader>
                    )}
                    {children}
                    <FooterGimmickBar />
                </ModalContent>
            </ModalPopupDialog>
            {backdrop && <Backdrop show={true} onClick={onHide} />}
        </ModalContainer>,

        document.getElementById('portal'),
    );
}

export const ModalContent = styled(ScrollBar)`
    @-moz-document url-prefix() {
        overflow-y: auto;
        scrollbar-color: var(--scroll-bar-thumb-moz) var(--scroll-bar-thumb);
    }
    background: var(--app-background);
    box-shadow: var(--container-shadow-color-400);

    border-radius: var(--modal-border-radius);
    pointer-events: fill;
    overflow-y: auto;
    position: relative;
    z-index: var(--z-index-modal-container);
    margin: auto;

    ${ModalHeader} {
        position: sticky;
        top: 0;
        left: 0;
    }

    ${ModalBody} {
        overflow: unset;
        position: relative;
        .dropdown-select__menu-list {
            height: var(--modal-dropdown-list-height);
        }
    }

    ${ModalFooter} {
        position: sticky;
        z-index: 1;
        bottom: 0;
        left: 0;
    }
    ${({popup, size}) => modalSize(popup, size)};
    ${({overflowUnset}) => {
        if (overflowUnset) return `overflow: unset`;
    }}
    ${({overflowNone}) => {
        if (overflowNone)
            return `
            @-moz-document url-prefix() {
                overflow: visible;
            }
            overflow: visible;
            max-height: 100%;
            height: unset;
            display: flex;
            flex-direction: column;
        
            ${ModalFooter},
            ${GradientGimmickBar} {
                border-radius: 0 0 var(--modal-border-radius) var(--modal-border-radius);
            }
            ${ModalBody} {
                max-height: calc(100vh - 160px);
                display: flex;
                flex-direction: column;
                padding-bottom: var(--modal-body-padding-bottom);
                border-radius: var(--modal-border-radius);
            }
        
            @media only screen and (max-height: 500px) {
                @-moz-document url-prefix() {
                    overflow: hidden;
                }
                overflow: hidden;
        
                ${ModalBody} {
                    overflow-y: auto;
                }
            }
            @media only screen and (min-height: 500px) {
                ${ModalFooter},
                ${GradientGimmickBar} {
                    z-index: var(--z-index-modal);
                }
            }
            `;
        else return ` `;
    }}
`;

export const ModalPopupDialog = styled.div`
    max-width: var(--modal-default-width);
    height: 100%;
    display: flex;
    flex-direction: column;
    -webkit-box-pack: center;
    justify-content: center;
    margin: auto;
    padding: var(--modal-padding);
`;

export const FooterGimmickBar = styled(GradientGimmickBar)`
    height: var(--modal-gimmick-bar-height);
    position: sticky;
    z-index: var(--z-index-modal-gimmick-bar);
    bottom: 0;
    left: 0;
`;
